import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FolderPlus, Globe, Lock, MessageSquare, Plus, Search } from 'lucide-react';
import { type FormEvent, useMemo, useState, useId } from 'react';
import { useMe } from '~/api/auth';
import { formError } from '~/api/client';
import { directoryQueryOptions } from '~/api/people';
import { useServerClock } from '~/api/serverClock';
import {
  IDEA_KIND_LABEL,
  type IdeaKind,
  listsQueryOptions,
  type Task,
  tasksQueryOptions,
  useCreateTask,
  useIdeaToProject,
  useUpdateTask,
} from '~/api/work';
import { TaskSheet } from '~/features/work/TaskSheet';
import { calendarDate, relativeTime } from '~/lib/format';
import { notify } from '~/lib/notify';
import { Button } from '~/ui/Button';
import { Card } from '~/ui/Card';
import { Chips } from '~/ui/Chips';
import { useConfirm } from '~/ui/Confirm';
import { Dialog } from '~/ui/Dialog';
import { EmptyState } from '~/ui/EmptyState';
import { ErrorNotice } from '~/ui/ErrorNotice';
import { Input } from '~/ui/Input';
import { Notice } from '~/ui/Notice';
import { PageHeader } from '~/ui/PageHeader';
import { Pill } from '~/ui/Pill';
import { Select } from '~/ui/Select';
import { Skeleton } from '~/ui/Skeleton';
import { Switch } from '~/ui/Switch';
import { Tabs } from '~/ui/Tabs';

type Scope = 'mine' | 'shared';
type KindFilter = 'all' | IdeaKind;

interface Search {
  scope: Scope;
  kind: KindFilter;
  task?: string;
}

export const Route = createFileRoute('/_app/ideas')({
  validateSearch: (s: Record<string, unknown>): Search => ({
    scope: s['scope'] === 'shared' ? 'shared' : 'mine',
    kind: s['kind'] === 'task' || s['kind'] === 'project' ? s['kind'] : 'all',
    task: typeof s['task'] === 'string' && s['task'] ? s['task'] : undefined,
  }),
  component: IdeasPage,
});

function IdeasPage() {
  const me = useMe();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/ideas' });
  const now = useServerClock(60_000);
  const today = calendarDate(now, me.person.tz ?? me.workspace.tz);
  const lists = useQuery(listsQueryOptions);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  const go = (patch: Partial<Search>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });

  const ideas = useQuery(
    tasksQueryOptions({
      status: 'idea',
      ideas: search.scope,
      ...(search.kind !== 'all' ? { ideaKind: search.kind } : {}),
      ...(q.trim() ? { q: q.trim() } : {}),
    }),
  );
  const directory = useQuery(directoryQueryOptions);
  const nameOf = useMemo(
    () => new Map((directory.data ?? []).map((p) => [p.id, p.name])),
    [directory.data],
  );

  return (
    <div className="mx-auto max-w-[1240px]">
      <PageHeader
        eyebrow="Work"
        title="Ideas"
        subtitle="Future tasks and projects. Keep an idea to yourself, or share it for everyone to discuss."
        actions={
          <Button icon={<Plus size={16} aria-hidden />} onClick={() => setAdding(true)}>
            New idea
          </Button>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Tabs
          label="Whose ideas"
          value={search.scope}
          onChange={(v) => go({ scope: v })}
          options={[
            { value: 'mine', label: 'My ideas' },
            { value: 'shared', label: 'Shared with everyone' },
          ]}
        />
        <Chips
          label="Kind"
          value={search.kind}
          onChange={(v) => go({ kind: v })}
          options={[
            { value: 'all', label: 'All' },
            { value: 'task', label: 'Task ideas' },
            { value: 'project', label: 'Project ideas' },
          ]}
        />
        <label className="relative ml-auto block w-full sm:w-64">
          <Search
            size={16}
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-3"
          />
          <input
            type="search"
            aria-label="Search ideas"
            placeholder="Search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-9 w-full rounded-full border border-line bg-paper pl-9 pr-3 text-sm placeholder:text-ink-3 focus:border-accent"
          />
        </label>
      </div>

      {ideas.isPending ? (
        <Skeleton lines={6} />
      ) : ideas.isError ? (
        <ErrorNotice error={ideas.error} onRetry={() => ideas.refetch()} />
      ) : ideas.data.length === 0 ? (
        <EmptyState
          title={search.scope === 'mine' ? 'No ideas of yours yet' : 'Nothing shared yet'}
          body={
            search.scope === 'mine'
              ? 'Write one down before it slips away. It stays private until you share it.'
              : 'When someone shares an idea, everyone can read it and comment here.'
          }
          action={
            <Button size="sm" icon={<Plus size={14} aria-hidden />} onClick={() => setAdding(true)}>
              New idea
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ideas.data.map((t, i) => (
            <IdeaCard
              key={t.id}
              idea={t}
              author={t.createdBy ? (nameOf.get(t.createdBy) ?? null) : null}
              mine={t.createdBy === me.person.id}
              delay={Math.min(i, 8) * 30}
              onOpen={() => go({ task: t.id })}
            />
          ))}
        </div>
      )}

      <NewIdeaDialog open={adding} onClose={() => setAdding(false)} />
      <TaskSheet
        id={search.task ?? null}
        lists={lists.data ?? []}
        today={today}
        onClose={() => go({ task: undefined })}
        onOpenRun={() => go({ task: undefined })}
        onOpenTask={(id) => go({ task: id })}
      />
    </div>
  );
}

function IdeaCard({
  idea: t,
  author,
  mine,
  delay,
  onOpen,
}: {
  idea: Task;
  author: string | null;
  mine: boolean;
  delay: number;
  onOpen: () => void;
}) {
  const me = useMe();
  const update = useUpdateTask();
  const toProject = useIdeaToProject();
  const confirm = useConfirm();
  const fail = (title: string) => (e: unknown) => notify.error(title, e);
  const canTeam = me.person.role !== 'member';

  return (
    <Card delay={delay}>
      <button type="button" onClick={onOpen} className="block w-full text-left">
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          <Pill tone={t.ideaKind === 'project' ? 'accent' : 'neutral'}>
            {IDEA_KIND_LABEL[t.ideaKind]}
          </Pill>
          {t.ideaShared ? (
            <Pill>
              <Globe size={11} aria-hidden className="mr-1 inline" />
              Shared
            </Pill>
          ) : (
            <Pill>
              <Lock size={11} aria-hidden className="mr-1 inline" />
              Private
            </Pill>
          )}
        </div>
        <p className="font-medium">{t.title}</p>
        {t.sop && <p className="mt-1 line-clamp-3 text-sm text-ink-2">{t.sop}</p>}
        <p className="mt-2 flex items-center gap-2 text-xs text-ink-3">
          <span>{mine ? 'You' : (author ?? 'Someone')}</span>
          <span>{relativeTime(t.createdAt)}</span>
          {t.commentCount > 0 && (
            <span className="inline-flex items-center gap-0.5">
              <MessageSquare size={12} aria-hidden /> {t.commentCount}
            </span>
          )}
        </p>
      </button>
      {mine && (
        <div className="mt-3 flex flex-wrap gap-2 border-t border-line pt-3">
          <Button
            variant="quiet"
            size="sm"
            icon={t.ideaShared ? <Lock size={14} aria-hidden /> : <Globe size={14} aria-hidden />}
            loading={update.isPending}
            onClick={() =>
              update
                .mutateAsync({ id: t.id, body: { ideaShared: !t.ideaShared } })
                .then(() =>
                  notify.success(
                    t.ideaShared ? 'Only you can see it now.' : 'Shared with everyone.',
                  ),
                )
                .catch(fail('Could not change who sees it.'))
            }
          >
            {t.ideaShared ? 'Make private' : 'Share'}
          </Button>
          {t.ideaKind === 'project' && (
            <Button
              variant="secondary"
              size="sm"
              icon={<FolderPlus size={14} aria-hidden />}
              loading={toProject.isPending}
              onClick={async () => {
                const kind = canTeam ? 'team' : 'personal';
                if (
                  !(await confirm({
                    title: `Start a ${kind} project from “${t.title}”?`,
                    body: 'The idea moves to the archive.',
                    confirmLabel: 'Start project',
                  }))
                )
                  return;
                try {
                  const res = await toProject.mutateAsync({ id: t.id, kind });
                  notify.success(`Project ${res.project.key} started.`);
                } catch (e) {
                  fail('Could not start the project.')(e);
                }
              }}
            >
              Start as a project
            </Button>
          )}
          {t.ideaKind === 'task' && (
            <Button variant="secondary" size="sm" onClick={onOpen}>
              Make it a task
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

function NewIdeaDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateTask();
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [kind, setKind] = useState<IdeaKind>('task');
  const [shared, setShared] = useState(false);
  const { fields: fieldErrors, message: formMessage } = formError(create.error);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await create.mutateAsync({
        title: title.trim(),
        status: 'idea',
        ideaKind: kind,
        ideaShared: shared,
        ...(notes.trim() ? { sop: notes.trim() } : {}),
      });
      notify.success(shared ? 'Idea shared.' : 'Idea saved. Only you can see it.');
      setTitle('');
      setNotes('');
      setKind('task');
      setShared(false);
      onClose();
    } catch {
      // shown inline
    }
  };

  // Unique per dialog: a page may hold several (one per card), all in the DOM.
  const newIdeaId = useId();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="What is the idea?"
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            form={newIdeaId}
            loading={create.isPending}
            disabled={!title.trim()}
          >
            Save idea
          </Button>
        </>
      }
    >
      <form id={newIdeaId} onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Input
          label="Idea"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          error={fieldErrors['title']}
        />
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Notes <span className="font-normal text-ink-3">(optional)</span>
          <textarea
            className="min-h-[90px] w-full rounded-control border border-line bg-paper px-3 py-2 font-normal focus:border-accent"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Why it matters, what it would take."
          />
        </label>
        <Select
          label="It would become"
          value={kind}
          onChange={(e) => setKind(e.target.value as IdeaKind)}
        >
          <option value="task">A task</option>
          <option value="project">A whole project</option>
        </Select>
        <Switch
          label="Share with everyone"
          hint={
            shared
              ? 'Everyone can read it and comment.'
              : 'Only you can see it. You can share it later.'
          }
          checked={shared}
          onChange={setShared}
        />
        {formMessage && <Notice tone="late">{formMessage}</Notice>}
      </form>
    </Dialog>
  );
}
